import { Funnel } from "@phosphor-icons/react";
import clsx from "clsx";
import { maxPageSize, usePaginatedDataSources } from "../../api/data-sources";
import { useLocations } from "../../api/location";
import {
    DateFilterValue,
    dateRangeFilterDropdownOptions,
    emissionsFactorOptions,
    scopeOptions,
} from "../../constants";
import useCollapsibleContent from "../../hooks/useCollapsibleContent";
import Button from "../Button";
import { DateRangeRadioFilterDropdown } from "../DateRangeFilterDropdown";
import FilterChip from "../FilterChip";
import MultiSelectDropdown, {
    MultiSelectDropdownItemOption,
} from "../MultiSelectFilterToolbarDropdown";
import ResponsiveGroupedItemsCarousel from "../ResponsiveGroupedItemsCarousel";
import SearchBar from "../SearchBar";
import Separator from "../Separator";
import FilterChipGroup from "../FilterChipGroup";
import { toggleItemInArray } from "../../utils/toggleItemInArray";
import { DataSourceSummary } from "../../api/models/DataSource";
import { Location } from "../../api/models/Location";

type ActivityDataFilterProps = {
    locationIds: number[];
    scopes: number[];
    dataSourceIds: number[];
    currentEmissionsFactorStatus: string | null;
    search: string;
    dateRange: DateFilterValue;
    setSearch: (value: string) => void;
    setScopes: (value: number[]) => void;
    setLocationIds: (value: number[]) => void;
    setDataSourceIds: (value: number[]) => void;
    setCurrentEmissionsFactorStatus: (value: string | null) => void;
    setDateRange: (value: DateFilterValue) => void;
};

const convertLocationsToMultiSelectDropdownOptions = (
    locations: Location[]
): MultiSelectDropdownItemOption<number>[] =>
    locations.map((location) => ({
        label: location.name,
        value: location.id,
    }));

const convertDataSourcesToMultiSelectDropdownOptions = (
    locations: DataSourceSummary[]
): MultiSelectDropdownItemOption<number>[] =>
    locations.map((location) => ({
        label: location.name,
        value: location.id,
    }));

const ActivityDataFilter = ({
    locationIds,
    setLocationIds,
    dataSourceIds,
    setDataSourceIds,
    scopes,
    setScopes,
    currentEmissionsFactorStatus,
    setCurrentEmissionsFactorStatus,
    search,
    setSearch,
    dateRange,
    setDateRange,
}: ActivityDataFilterProps) => {
    const { locations } = useLocations();
    const { dataSources } = usePaginatedDataSources(maxPageSize);

    const { expanded, toggle, triggerProps, expandableContentProps } =
        useCollapsibleContent();

    const locationOptions =
        convertLocationsToMultiSelectDropdownOptions(locations);

    const dataSourceOptions =
        convertDataSourcesToMultiSelectDropdownOptions(dataSources);

    const handleLocationSelect = (value: number) =>
        setLocationIds(toggleItemInArray(locationIds, value));

    const handleDataSourceSelect = (value: number) =>
        setDataSourceIds(toggleItemInArray(dataSourceIds, value));

    const handleScopeSelect = (value: number) =>
        setScopes(toggleItemInArray(scopes, value));

    const clearAllFilters = () => {
        setSearch("");
        setLocationIds([]);
        setScopes([]);
        setDataSourceIds([]);
        setCurrentEmissionsFactorStatus(null);
    };

    const showClearAllButton =
        search !== "" ||
        locationIds.length > 0 ||
        dataSourceIds.length > 0 ||
        scopes.length > 0 ||
        currentEmissionsFactorStatus !== null;

    const filterDropdowns = [
        <DateRangeRadioFilterDropdown
            value={dateRange}
            onChange={setDateRange}
            key="filter-dropdown-date-range"
        />,
        <MultiSelectDropdown
            label="Location"
            selected={locationIds}
            options={locationOptions}
            onSelect={handleLocationSelect}
            onClear={() => setLocationIds([])}
            key="filter-dropdown-locations"
        />,
        <MultiSelectDropdown
            label="Asset"
            selected={dataSourceIds}
            options={dataSourceOptions}
            onSelect={handleDataSourceSelect}
            onClear={() => setDataSourceIds([])}
            key="filter-dropdown-data-source"
        />,
        <MultiSelectDropdown
            label="Scope"
            selected={scopes}
            options={scopeOptions}
            onSelect={handleScopeSelect}
            onClear={() => setScopes([])}
            key="filter-dropdown-scope"
        />
    ];

    const currentEmissionsFactorStatusLabel = emissionsFactorOptions.find(
        (emissionsFactor) =>
            emissionsFactor.value === currentEmissionsFactorStatus
    )?.label;

    const dateRangeFilterLabel = dateRangeFilterDropdownOptions.find(
        (option) => option.value === dateRange
    )?.label;

    return (
        <>
            <div className="flex items-center p-5">
                <Button
                    variant="outline"
                    onClick={toggle}
                    {...triggerProps}
                    className="flex-none"
                >
                    <Funnel width={20} height={20} className="mr-2" />
                    {expanded ? "Hide" : "Show"} filters & sorting
                </Button>
                <div className="flex gap-2 flex-wrap pl-2">
                    {search !== "" && (
                        <FilterChip onRemove={() => setSearch("")}>
                            '{search}'
                        </FilterChip>
                    )}
                    {dateRange !== "all" && (
                        <FilterChip onRemove={() => setDateRange("all")}>
                            {dateRangeFilterLabel}
                        </FilterChip>
                    )}
                    <FilterChipGroup
                        allItems={locations}
                        selectedItemIds={locationIds}
                        labelKey="name"
                        onRemove={setLocationIds}
                    />
                    <FilterChipGroup
                        selectedItemIds={dataSourceIds}
                        allItems={dataSources}
                        labelKey="name"
                        onRemove={setDataSourceIds}
                    />
                    <FilterChipGroup
                        allItems={scopeOptions.map((scope) => ({
                            ...scope,
                            id: scope.value,
                        }))}
                        selectedItemIds={scopes}
                        labelKey="label"
                        onRemove={setScopes}
                    />
                    {currentEmissionsFactorStatus !== null && (
                        <FilterChip
                            onRemove={() =>
                                setCurrentEmissionsFactorStatus(null)
                            }
                        >
                            {currentEmissionsFactorStatusLabel}
                        </FilterChip>
                    )}
                    {showClearAllButton && (
                        <Button variant="outline" onClick={clearAllFilters}>
                            Clear all
                        </Button>
                    )}
                </div>
            </div>
            <Separator />
            <div
                className={clsx("pt-5 px-6", !expanded && "hidden")}
                {...expandableContentProps}
            >
                <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Search..."
                    additionalSearchElements={
                        <ResponsiveGroupedItemsCarousel
                            items={filterDropdowns}
                            prevTooltipLabel="See previous group of filters"
                            nextTooltipLabel="See next group of filters"
                        />
                    }
                />
            </div>
        </>
    );
};

export default ActivityDataFilter;
